/// <amd-module name="Feed/Legacy/Components/Settings/UpstreamSettingsPane" />
import * as React from "react";

import { PrimaryButton } from "OfficeFabric/Button";

import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Filter } from "VSSUI/Utilities/Filter";

import { SettingsPivotKeys } from "Feed/Common/Constants/Constants";
import { UpstreamSource } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { FeedSettingsActionCreator } from "Feed/Legacy/Actions/FeedSettingsActionCreator";
import { NoResultsPane } from "Feed/Legacy/Components/NoResultsPane";
import { AddUpstreamPanel } from "Feed/Legacy/Components/Settings/AddUpstreamPanel";
import { IInternalUpstreamSettingsState } from "Feed/Legacy/Components/Settings/IFeedSettingsState";
import { UpstreamSettingsList } from "Feed/Legacy/Components/Settings/UpstreamSettingsList";
import { IPackageProtocol } from "Feed/Legacy/Protocols/Common/IPackageProtocol";
import { IUpstreamSettingsRowData } from "Feed/Legacy/Types/IUpstreamSettingsRowData";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

export interface IUpstreamSettingsPaneProps {
    pageContext: IVssPageContext;
    /**
     * The description for the pivot
     */
    description: string;

    /**
     * The filter associated with the pivot's filter bar
     */
    filter: Filter;

    /**
     * There are no upstream sources
     */
    hasNoUpstreamSources: boolean;

    /**
     * No upstream sources after filtering
     */
    hasNoFilterResults: boolean;

    /**
     * List of upstream sources to display to the user, may include unsaved changes
     */
    upstreamSourceRows: IUpstreamSettingsRowData[];

    /**
     * True if the current user can make changes to the feed settings
     */
    isUserAdmin: boolean;

    /**
     * Protocols to filter on
     */
    protocolFilterValues: IPackageProtocol[];

    /**
     * Error message to display on upstream source name field in the AddUpstreamPanel
     */
    upstreamSourceNameInvalidMessage?: string;

    /**
     * Error message to display on upstream source location field in the AddUpstreamPanel
     */
    upstreamSourceLocationInvalidMessage?: string;

    /**
     * True to display the AddUpstreamPanel
     */
    displayAddUpstreamPanel: boolean;

    /**
     * List of feeds in the account
     */
    availableFeeds: () => Feed[];

    /**
     * Currently selected feed
     */
    currentFeed: Feed;

    /**
     * List of active upstream sources for the selected feed
     */
    activeUpstreamSources: UpstreamSource[];

    /**
     * Internal upstream feature availability
     */
    internalUpstreamSettings: IInternalUpstreamSettingsState;

    /**
     * Tracks if views are getting saved to server
     */
    isSavingChanges: boolean;

    /**
     * While saving, captures errors if they occur to show in UI
     */
    error: Error;

    /**
     * Feature flag for custom upstream sources
     */
    isCustomPublicUpstreamsFeatureEnabled: boolean;

    /**
     * Feature flag for project scoped upstream sources
     */
    isProjectScopedUpstreamsEnabled: boolean;

    /**
     * Maximum number of upstream sources
     */
    upstreamSourceLimit: number;

    /**
     * Feature flag for Upstreams with Service Endpoints
     */
    isServiceEndpointUpstreamsFeatureEnabled: boolean;
}

export class UpstreamSettingsPane extends React.PureComponent<IUpstreamSettingsPaneProps> {
    public render(): JSX.Element {
        return (
            <div className="upstream-settings-pane">
                {this._getUpstreamLimitReachedMessage()}
                {this._getUpstreamListOrMessage()}
                {this.props.displayAddUpstreamPanel ? (
                    <AddUpstreamPanel
                        pageContext={this.props.pageContext}
                        upstreamSourceNameInvalidMessage={this.props.upstreamSourceNameInvalidMessage}
                        upstreamSourceLocationInvalidMessage={this.props.upstreamSourceLocationInvalidMessage}
                        currentFeed={this.props.currentFeed}
                        availableFeedsForUpstreams={this.getAvailableUpstreams()}
                        internalUpstreamSettings={this.props.internalUpstreamSettings}
                        activeUpstreamSources={this.props.activeUpstreamSources}
                        isSavingChanges={this.props.isSavingChanges}
                        error={this.props.error}
                        isCustomPublicUpstreamsFeatureEnabled={this.props.isCustomPublicUpstreamsFeatureEnabled}
                        isServiceEndpointUpstreamsFeatureEnabled={this.props.isServiceEndpointUpstreamsFeatureEnabled}
                    />
                ) : null}
            </div>
        );
    }

    private _getUpstreamLimitReachedMessage(): JSX.Element {
        const mapCount: { [key: string]: number } = {};
        const limit: number = this.props.upstreamSourceLimit;

        this.props.activeUpstreamSources.forEach(src => {
            const p = src.protocol.toLowerCase();
            // tslint:disable-next-line:no-bitwise
            mapCount[p] = (mapCount[p] | 0) + 1;
        });

        const protocolList: string[] = [];
        for (const protocol in mapCount) {
            if (mapCount[protocol] >= limit) {
                protocolList.push(protocol);
            }
        }

        if (protocolList.length > 0) {
            return (
                <MessageBar severity={MessageBarSeverity.Warning}>
                    {protocolList.map((protocol, index) => {
                        return (
                            <div key={protocol + "-source-limit-reached"}>
                                {Utils_String.format(PackageResources.UpstreamSourcesLimitReached, protocol)}
                            </div>
                        );
                    })}
                </MessageBar>
            );
        }
        return null;
    }
    /**
     * Returns the upstream list component, or a message if there are no upstream sources
     */
    private _getUpstreamListOrMessage(): JSX.Element {
        if (this.props.hasNoUpstreamSources) {
            return (
                <NoResultsPane
                    pageContext={this.props.pageContext}
                    header={PackageResources.UpstreamSettings_NoUpstreamSources}
                    subheader={this.props.description}
                    iconClass={"bowtie-cloud-fill"}
                >
                    {this.props.isUserAdmin ? (
                        <PrimaryButton
                            className="add-upstreamsource-button"
                            iconProps={{ iconName: "Add" }}
                            ariaLabel={PackageResources.UpstreamSettings_AddUpstreamSourceButton_Label}
                            onClick={() => {
                                FeedSettingsActionCreator.openAddUpstreamPanelRequested.invoke({});
                            }}
                        >
                            {PackageResources.UpstreamSettings_AddUpstreamSourceButton_Label}
                        </PrimaryButton>
                    ) : (
                            <div />
                        )}
                </NoResultsPane>
            );
        }

        if (this.props.hasNoFilterResults) {
            return (
                <NoResultsPane
                    pageContext={this.props.pageContext}
                    header={PackageResources.UpstreamSettings_NoUpstreamSourcesMatch}
                    iconClass={"bowtie-search"}
                />
            );
        }

        return (
            <UpstreamSettingsList
                upstreamSourceRows={this.props.upstreamSourceRows}
                isUserAdmin={this.props.isUserAdmin}
                isSavingChanges={this.props.isSavingChanges}
                pageContext={this.props.pageContext}
                feed={this.props.currentFeed}
            />
        );
    }

    protected getPivotKey(): string {
        return SettingsPivotKeys.upstreams;
    }

    private getAvailableUpstreams(): Feed[] {
        let result = this.props
            .availableFeeds()
            .filter((feed: Feed) => feed.id !== this.props.currentFeed.id && feed.deletedDate == null);
        if (!this.props.isProjectScopedUpstreamsEnabled) {
            result = result.filter((feed: Feed) => !feed.project);
        }
        return result;
    }
}
